/**
 * Hook para gerenciar testes A/B de headline
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import * as headlineTestService from '@/services/headlineTestService';

// Query keys para cache
export const HEADLINE_TEST_KEYS = {
  all: ['headline-tests'] as const,
  list: (userId: string) => [...HEADLINE_TEST_KEYS.all, 'list', userId] as const,
  detail: (testId: string) => [...HEADLINE_TEST_KEYS.all, 'detail', testId] as const,
  metrics: (testId: string, startDate: string, endDate: string) =>
    [...HEADLINE_TEST_KEYS.all, 'metrics', testId, startDate, endDate] as const,
};

/**
 * Hook para buscar todos os testes de headline de um usuário
 */
export const useUserHeadlineTests = (userId: string | undefined) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: HEADLINE_TEST_KEYS.list(userId || ''),
    queryFn: () => headlineTestService.findUserHeadlineTests(userId!, token!),
    enabled: !!userId && !!token,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
};

/**
 * Hook para criar um novo teste de headline
 */
export const useCreateHeadlineTest = () => {
  const queryClient = useQueryClient();
  const { user, token } = useAuth();

  return useMutation({
    mutationFn: (payload: {
      originalVideoId: string;
      headlineTestData: headlineTestService.CreateHeadlineTestPayload['headlineTestData'];
      headlineTestConfig: headlineTestService.CreateHeadlineTestPayload['headlineTestConfig'];
    }) => {
      return headlineTestService.createHeadlineTest(
        payload.originalVideoId,
        {
          headlineTestData: payload.headlineTestData,
          headlineTestConfig: payload.headlineTestConfig,
        },
        token!
      );
    },
    onSuccess: () => {
      // Invalidar cache para refetch
      if (user) {
        queryClient.invalidateQueries({
          queryKey: HEADLINE_TEST_KEYS.list(user.id_user || user.id)
        });
      }
    },
  });
};

/**
 * Hook para deletar um teste de headline
 */
export const useDeleteHeadlineTest = () => {
  const queryClient = useQueryClient();
  const { user, token } = useAuth();

  return useMutation({
    mutationFn: (headlineTestId: string) => {
      return headlineTestService.deleteHeadlineTest(headlineTestId, token!);
    },
    onSuccess: () => {
      // Invalidar cache para refetch
      if (user) {
        queryClient.invalidateQueries({
          queryKey: HEADLINE_TEST_KEYS.list(user.id_user || user.id)
        });
      }
    },
  });
};

/**
 * Hook para buscar métricas de um teste
 */
export const useHeadlineTestMetrics = (
  headlineTestId: string | undefined,
  startDate: string,
  endDate: string
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: HEADLINE_TEST_KEYS.metrics(headlineTestId || '', startDate, endDate),
    queryFn: () =>
      headlineTestService.getHeadlineTestMetrics(headlineTestId!, startDate, endDate, token!),
    enabled: !!headlineTestId && !!token && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 2, // 2 minutos
  });
};

/**
 * Hook para atualizar título de um teste
 */
export const useUpdateHeadlineTestTitle = () => {
  const queryClient = useQueryClient();
  const { user, token } = useAuth();

  return useMutation({
    mutationFn: (payload: { headlineTestId: string; title: string }) => {
      return headlineTestService.updateHeadlineTestTitle(
        payload.headlineTestId,
        { headlineTestTitle: payload.title },
        token!
      );
    },
    onSuccess: () => {
      // Invalidar cache para refetch
      if (user) {
        queryClient.invalidateQueries({
          queryKey: HEADLINE_TEST_KEYS.list(user.id_user || user.id)
        });
      }
    },
  });
};
